import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, ChevronRight, Award, Quote } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

/* Logo/foto: i valori placeholder o vuoti non vanno resi come immagine. */
const isRealImg = (url) => !!url && !String(url).startsWith("PLACEHOLDER");

const Initials = ({ nome, cognome }) => (
  <div
    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold text-sm"
    aria-hidden="true"
  >
    {`${(nome || "")[0] || ""}${(cognome || "")[0] || ""}`.toUpperCase()}
  </div>
);

const ExpertAvatar = ({ expert, size = "h-11 w-11" }) => {
  const src = isRealImg(expert.logo_url) ? expert.logo_url : isRealImg(expert.foto_url) ? expert.foto_url : null;
  if (src) {
    return (
      <img
        src={src}
        alt={`${expert.brand || expert.nome}`}
        className={`${size} shrink-0 rounded-full object-cover bg-white`}
      />
    );
  }
  return <Initials nome={expert.nome} cognome={expert.cognome} />;
};

/**
 * Badge "Contenuti validati da un esperto" mostrato in testa a un modulo le cui aree
 * intersecano quelle di un esperto ATTIVO. Cliccando si apre la scheda dell'esperto.
 * Se non ci sono esperti attivi per il modulo, non rende nulla.
 */
export const ExpertBadge = ({ moduleId }) => {
  const [experts, setExperts] = useState([]);

  useEffect(() => {
    if (!moduleId) return;
    let alive = true;
    api
      .get(`/experts/module/${moduleId}`)
      .then((r) => alive && setExperts(Array.isArray(r.data) ? r.data : []))
      .catch(() => alive && setExperts([]));
    return () => {
      alive = false;
    };
  }, [moduleId]);

  if (!experts.length) return null;
  const expert = experts[0]; // primo garante per 'ordine'
  const citazione = (expert.citazioni || [])[0]?.testo;

  return (
    <div className="mt-5 fade-up" data-testid="expert-badge">
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid="expert-badge-trigger"
            className="press group flex w-full items-center gap-3 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--ink)] px-4 py-3 text-left text-white transition-colors duration-200 hover:bg-black/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--volt)]"
          >
            <ExpertAvatar expert={expert} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-[color:var(--volt)]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[color:var(--volt)]">
                  Contenuti validati da un esperto
                </span>
              </div>
              <p className="mt-0.5 truncate text-sm font-extrabold leading-5">
                {expert.nome} {expert.cognome}
              </p>
              <p className="truncate text-xs text-white/70">
                {[expert.ruolo, expert.brand].filter(Boolean).join(" · ")}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-white/60 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        </DialogTrigger>

        <DialogContent
          className="max-w-md gap-0 overflow-hidden p-0"
          data-testid="expert-card-dialog"
        >
          {/* Header navy con avatar + identità */}
          <div className="bg-[color:var(--ink)] px-5 py-5 text-white">
            <DialogHeader className="space-y-0">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--volt)]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[color:var(--volt)]">
                  Esperto garante
                </span>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <ExpertAvatar expert={expert} size="h-14 w-14" />
                <div className="min-w-0">
                  <DialogTitle className="truncate text-lg font-extrabold leading-tight text-white">
                    {expert.nome} {expert.cognome}
                  </DialogTitle>
                  <p className="truncate text-xs text-white/75">{expert.ruolo}</p>
                  <p className="truncate text-xs text-white/60">
                    {[expert.brand, expert.citta].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </div>
            </DialogHeader>
          </div>

          {/* Corpo scheda */}
          <div className="max-h-[60vh] space-y-5 overflow-y-auto px-5 py-5">
            {expert.bio && (
              <p className="text-sm leading-6 text-[color:var(--ink)]">{expert.bio}</p>
            )}

            {(expert.credenziali || []).length > 0 && (
              <div>
                <h4 className="mb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-2)]">
                  Credenziali
                </h4>
                <ul className="space-y-2">
                  {expert.credenziali.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Award className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--ink)]" />
                      <span>
                        <span className="font-bold">{c.nome}</span>
                        {c.ente ? <span className="text-[color:var(--text-2)]"> · {c.ente}</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {citazione && (
              <blockquote className="relative rounded-2xl border-l-2 border-[color:var(--volt)] bg-[color:var(--bg-subtle)] px-4 py-3">
                <Quote className="mb-1 h-4 w-4 text-[color:var(--ink)]" />
                <p className="text-sm italic leading-6 text-[color:var(--ink)]">{citazione}</p>
              </blockquote>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
